import { DialogComponent } from '@theme/dialog';

/**
 * Kit Customization Modal Component
 * Extends DialogComponent similar to CartDrawerComponent
 */
class KitCustomizationComponent extends DialogComponent {
  constructor() {
    super();
    this.selectedColors = {};
    this.totalSelected = 0;
    this.maxQuantity = 0;
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize component
    this.initializeElements();
    this.setupQuantityControls();

    // Setup triggers
    const trigger = document.querySelector('[data-kit-customization-trigger]');
    if (trigger) {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    }

    // Setup close button
    const closeButton = this.querySelector('[data-kit-modal-close]');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.close());
    }

    // Setup add to cart
    const addToCartButton = this.querySelector('[data-kit-add-to-cart]');
    if (addToCartButton) {
      addToCartButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }
  }

  initializeElements() {
    // Get elements
    this.dialog = this.querySelector('[data-kit-modal]');
    this.addToCartButton = this.querySelector('[data-kit-add-to-cart]');
    this.colorsGrid = this.querySelector('[data-kit-colors-grid]');
    this.errorContainer = this.querySelector('[data-kit-error]');
    this.selectedQuantityEl = this.querySelector('[data-kit-selected-quantity]');
    this.priceValueEl = this.querySelector('[data-kit-price-value]');

    // Get kit data from parent block
    const kitBlock = document.querySelector('[data-kit-customization]');
    if (kitBlock) {
      this.productId = kitBlock.dataset.productId;
      this.maxQuantity = parseInt(kitBlock.dataset.kitQuantity) || 0;
    }
  }

  open() {
    // Save original button text
    if (this.addToCartButton) {
      const textEl = this.addToCartButton.querySelector('.kit-dialog__submit-text');
      if (textEl && !this.addToCartButton.dataset.originalText) {
        this.addToCartButton.dataset.originalText = textEl.textContent;
      }
    }

    // Show dialog using parent class method
    this.showDialog();

    // Update button state
    this.updateAddToCartButton();
  }

  close() {
    this.closeDialog();
  }

  setupQuantityControls() {
    const colorItems = this.colorsGrid?.querySelectorAll('[data-color-item]') || [];

    colorItems.forEach(item => {
      const minusButton = item.querySelector('[data-kit-quantity-minus]');
      const plusButton = item.querySelector('[data-kit-quantity-plus]');
      const input = item.querySelector('[data-kit-quantity-input]');
      const color = item.dataset.color;

      // Initialize color in selectedColors
      this.selectedColors[color] = 0;

      // Minus button
      minusButton?.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 0;
        if (currentValue > 0) {
          this.updateQuantity(color, currentValue - 1, input, minusButton, plusButton);
        }
      });

      // Plus button
      plusButton?.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 0;
        if (this.totalSelected < this.maxQuantity) {
          this.updateQuantity(color, currentValue + 1, input, minusButton, plusButton);
        } else {
          this.showError(`Você já selecionou o máximo de ${this.maxQuantity} laços para o kit`);
        }
      });
    });
  }

  updateQuantity(color, value, input, minusButton, plusButton) {
    // Update value
    this.selectedColors[color] = value;
    input.value = value;

    // Update total
    this.totalSelected = Object.values(this.selectedColors).reduce((sum, qty) => sum + qty, 0);

    // Update UI
    this.updateSelectedQuantity();
    this.updateButtonStates(value, minusButton, plusButton);
    this.updateAddToCartButton();
    this.hideError();

    // Add visual feedback to the color card
    const colorCard = input.closest('[data-color-item]');
    if (colorCard) {
      if (value > 0) {
        colorCard.classList.add('kit-color-card--selected');
      } else {
        colorCard.classList.remove('kit-color-card--selected');
      }
    }
  }

  updateButtonStates(value, minusButton, plusButton) {
    // Disable minus if at 0
    minusButton.disabled = value === 0;

    // Disable plus if at max
    plusButton.disabled = this.totalSelected >= this.maxQuantity;
  }

  updateSelectedQuantity() {
    if (this.selectedQuantityEl) {
      this.selectedQuantityEl.textContent = this.totalSelected;

      // Add visual feedback for completed selection
      const infoRow = this.selectedQuantityEl.closest('.kit-dialog__info-row');
      if (infoRow) {
        if (this.totalSelected === this.maxQuantity) {
          infoRow.classList.add('kit-dialog__info-row--complete');
        } else {
          infoRow.classList.remove('kit-dialog__info-row--complete');
        }
      }
    }
  }

  updateAddToCartButton() {
    if (this.addToCartButton) {
      const textEl = this.addToCartButton.querySelector('.kit-dialog__submit-text');
      this.addToCartButton.disabled = this.totalSelected !== this.maxQuantity;

      if (this.totalSelected < this.maxQuantity) {
        const remaining = this.maxQuantity - this.totalSelected;
        if (textEl) {
          textEl.textContent = `Selecione mais ${remaining} ${remaining === 1 ? 'laço' : 'laços'}`;
        }
      } else if (this.totalSelected === this.maxQuantity) {
        if (textEl) {
          textEl.textContent = this.addToCartButton.dataset.originalText || 'Adicionar ao Carrinho';
        }
      }
    }
  }

  showError(message) {
    if (this.errorContainer) {
      const errorText = this.errorContainer.querySelector('.kit-dialog__error-text');
      if (errorText) {
        errorText.textContent = message;
      }
      this.errorContainer.hidden = false;

      // Hide after 3 seconds
      setTimeout(() => {
        this.hideError();
      }, 3000);
    }
  }

  hideError() {
    if (this.errorContainer) {
      this.errorContainer.hidden = true;
    }
  }

  async addToCart() {
    // Re-get the button in case it wasn't initialized
    if (!this.addToCartButton) {
      this.addToCartButton = this.querySelector('[data-kit-add-to-cart]');
    }

    if (!this.addToCartButton || this.totalSelected !== this.maxQuantity) {
      console.log('Cannot add to cart:', {
        hasButton: !!this.addToCartButton,
        totalSelected: this.totalSelected,
        maxQuantity: this.maxQuantity
      });
      return;
    }

    const loadingEl = this.addToCartButton.querySelector('.kit-dialog__submit-loading');
    const textEl = this.addToCartButton.querySelector('.kit-dialog__submit-text');

    try {
      // Show loading state
      this.addToCartButton.disabled = true;
      if (loadingEl) loadingEl.hidden = false;
      if (textEl) textEl.hidden = true;

      // Get variant ID
      const variantId = this.addToCartButton.dataset.variantId;

      // Prepare properties for the cart
      const properties = {

      };

      // Add color details as individual properties
      Object.entries(this.selectedColors).forEach(([color, quantity]) => {
        if (quantity > 0) {
          properties[`Cor: ${color}`] = `${quantity} ${quantity === 1 ? 'laço' : 'laços'}`;
        }
      });

      // Add to cart via AJAX API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: properties
          }],
          sections: "carrinho-ajax"
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const data = await response.json();

      // Update cart UI
      await this.updateCartUI(data);

      // Show success and close modal
      this.showSuccess();
      setTimeout(() => {
        this.close();
        this.resetForm();
      }, 1500);

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError('Erro ao adicionar ao carrinho. Por favor, tente novamente.');
    } finally {
      // Reset loading state
      this.addToCartButton.disabled = false;
      if (loadingEl) loadingEl.hidden = true;
      if (textEl) textEl.hidden = false;
    }
  }

  async updateCartUI(data) {
    // Fetch updated cart
    let divTemporaria = document.createElement('div');
    let carrinho = data.sections["carrinho-ajax"];
    divTemporaria.innerHTML = carrinho;

    let carrinhoContent = divTemporaria.querySelector('.cart-drawer__content')?.innerHTML;
    let carrinhoHeader = divTemporaria.querySelector('.cart-drawer__header')?.innerHTML;
    let cartBubble = divTemporaria.querySelector('.cart-bubble__text-count')?.innerHTML;

    if (carrinhoContent) {
      const contentEl = document.querySelector('.cart-drawer__content');
      if (contentEl) contentEl.innerHTML = carrinhoContent;
    }

    if (carrinhoHeader) {
      const headerEl = document.querySelector('.cart-drawer__header');
      if (headerEl) headerEl.innerHTML = carrinhoHeader;
    }

    if (cartBubble) {
      const bubbleEl = document.querySelector('.cart-bubble .cart-bubble__text-count');
      if (bubbleEl) bubbleEl.innerHTML = cartBubble;
    }

    let elemento = document.querySelector('.cart-bubble');
    let elemento_dois = document.querySelector('.cart-bubble .cart-bubble__text-count');

    if (elemento?.classList.contains('visually-hidden')) {
      elemento.classList.remove('visually-hidden');
    }

    if (elemento_dois?.classList.contains('hidden')) {
      elemento_dois.classList.remove('hidden');
    }
  }

  showSuccess() {
    // Update status element
    const statusEl = document.querySelector('[data-kit-status]');
    if (statusEl) {
      const statusText = statusEl.querySelector('.kit-customization-status__text');
      if (statusText) {
        statusText.textContent = 'Kit adicionado ao carrinho com sucesso!';
      }
      statusEl.hidden = false;
      statusEl.classList.add('kit-customization-status--success');

      setTimeout(() => {
        statusEl.hidden = true;
        statusEl.classList.remove('kit-customization-status--success');
      }, 3000);
    }
  }

  resetForm() {
    // Reset all quantities
    this.selectedColors = {};
    this.totalSelected = 0;

    const inputs = this.colorsGrid?.querySelectorAll('[data-kit-quantity-input]') || [];
    inputs.forEach(input => {
      input.value = 0;
      const color = input.dataset.color;
      if (color) {
        this.selectedColors[color] = 0;
      }

      // Remove selected class from color cards
      const colorCard = input.closest('[data-color-item]');
      if (colorCard) {
        colorCard.classList.remove('kit-color-card--selected');
      }
    });

    // Reset buttons
    const minusButtons = this.colorsGrid?.querySelectorAll('[data-kit-quantity-minus]') || [];
    minusButtons.forEach(btn => btn.disabled = true);

    const plusButtons = this.colorsGrid?.querySelectorAll('[data-kit-quantity-plus]') || [];
    plusButtons.forEach(btn => btn.disabled = false);

    // Update UI
    this.updateSelectedQuantity();
    this.updateAddToCartButton();
  }
}

// Register the custom element
if (!customElements.get('kit-customization-component')) {
  customElements.define('kit-customization-component', KitCustomizationComponent);
}